import { useEffect, useId, useLayoutEffect, useRef, useState, type ComponentType, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react';
import { IconEllipsisVertical, type IconProps } from '../icons';
import { IconButton } from './Button';
import { opensUp, type Box } from './menuPlacement';

export interface RowMenuItem {
  label: string;
  icon?: ComponentType<IconProps>;
  onSelect: () => void;
  danger?: boolean;
  disabled?: boolean;
}

/**
 * The kebab menu, which exists for one reason: to get delete out from under the cursor.
 *
 * <p>A visible bin icon on every row of a table is a mis-click away from taking a question
 * out of thirteen templates. Editing stays in the open; the irreversible things live one
 * deliberate click deeper, at the bottom, in red, behind a confirmation.</p>
 */
export function RowMenu({
  label,
  items,
  trigger,
}: {
  label: string;
  items: RowMenuItem[];

  /**
   * Something other than the kebab to open it with — a labelled button, say.
   *
   * <p>Here rather than in a second menu component. What makes a menu correct is the part
   * nobody sees: focus into the first item, arrows between them, Escape back to the
   * trigger, a click outside closing it. Writing that twice means fixing it twice, and the
   * copy that is not in front of anybody is the one that stays broken.</p>
   */
  trigger?: (open: boolean, toggle: () => void, menuId: string) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const [up, setUp] = useState(false);

  // Opens upwards when the box that would cut it off has no room below: a table's scroll box
  // (Pozisyonlar, 2 November) or an audit category card (photo menu, 4 November).
  useLayoutEffect(() => {
    if (!open || !menuRef.current || !rootRef.current) return;
    // Every ancestor's overflow, nearest first: whichever hides or scrolls is the one that
    // would cut the menu off. See opensUp for the case that asked for it.
    const ancestors: Box[] = [];
    for (let box = rootRef.current.parentElement; box; box = box.parentElement) {
      const style = getComputedStyle(box);
      ancestors.push({ overflow: `${style.overflowX} ${style.overflowY}`, bottom: box.getBoundingClientRect().bottom });
    }
    setUp(opensUp(rootRef.current.getBoundingClientRect().bottom, menuRef.current.offsetHeight, ancestors, window.innerHeight));
  }, [open]);

  useEffect(() => {
    if (!open) return;

    menuRef.current?.querySelector<HTMLButtonElement>('button:not(:disabled)')?.focus();

    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      rootRef.current?.querySelector<HTMLButtonElement>('button')?.focus();
    };

    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);

    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const onMenuKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();

    const buttons = [...(menuRef.current?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)') ?? [])];
    const index = buttons.indexOf(document.activeElement as HTMLButtonElement);
    buttons[(index + (e.key === 'ArrowDown' ? 1 : -1) + buttons.length) % buttons.length]?.focus();
  };

  return (
    <div className="nv-menu-anchor" ref={rootRef}>
      {trigger ? (
        trigger(open, () => setOpen((o) => !o), menuId)
      ) : (
        <IconButton
          label={label}
          icon={IconEllipsisVertical}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-controls={open ? menuId : undefined}
          tooltip={!open}
          onClick={() => setOpen((o) => !o)}
        />
      )}
      {open ? (
        <div className={up ? 'nv-menu nv-menu--up' : 'nv-menu'} role="menu" id={menuId} ref={menuRef} onKeyDown={onMenuKeyDown}>
          {items.map((item, i) => (
            <div key={item.label} className="nv-menu__slot">
              {item.danger && i > 0 ? <div className="nv-menu__sep" role="separator" /> : null}
              <button
                type="button"
                role="menuitem"
                disabled={item.disabled}
                className={item.danger ? 'nv-menu__item nv-menu__item--danger' : 'nv-menu__item'}
                onClick={() => {
                  setOpen(false);
                  item.onSelect();
                }}
              >
                {item.icon ? <item.icon size={16} /> : null}
                {item.label}
              </button>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
